import { useMemo } from 'react';
import { useSettingSwitch } from './TenantSettingSwitch';
import { ISettingType, ISettingToUpdate } from '../../interfaces/TenantSetting';
import useSettingCurrencySelect from './TenantSettingCurrencySelect';
import useSettingLanguageSelect from './TenantSettingLanguageSelect';
import { useSettingNumberInput } from './TenantSettingNumberInput';
import useSettingReceiptTaxTypeSelect from './TenantSettingReceiptTaxTypeSelect';
import useSettingAllReceiptTypes from './TenantSettingAllReceiptTypes';
import { useSettingStringArrayInput } from './TenantSettingStringArrayInput';
import { useSettingEmailArrayInput } from './TenantSettingEmailArrayInput';
import { useSettingReceiptDateEnum } from './TenantSettingReceiptDateEnumSelect';
import { useSettingReceiptDescriptionEnum } from './TenantSettingReceiptDescriptionEnumSelect';
export enum SettingType {
  switch = 'switch',
  currency = 'currency',
  language = 'language',
  number = 'number',
  receipt_tax_type = 'receipt_tax_type',
  all_receipt_types = 'all_receipt_types',
  stringArray = 'stringArray',
  emailArray = 'emailArray',
  receiptDateEnum = 'receipt_date_enum',
  receiptDescriptionEnum = 'receipt_description_enum',
}

type DynamicSettingProps = {
  settingsArray: ISettingToUpdate[];
  handleChange: (id: any, value: any) => void;
  setValidationState: (isValid: boolean) => void;
  isSuperAdmin: boolean;
};

export const useDynamicSetting = (props: DynamicSettingProps) => {
  const { settingsArray, handleChange, setValidationState, isSuperAdmin } =
    props;
  const { languageSetting } = useSettingLanguageSelect({
    settingsArray,
    handleChange,
  });
  const { currencyTypeSetting, currencies } = useSettingCurrencySelect({
    settingsArray,
    handleChange,
  });
  const { booleanTypeSetting } = useSettingSwitch({
    settingsArray,
    handleChange,
  });
  const { numberTypeSetting } = useSettingNumberInput({
    settingsArray,
    handleChange,
  });
  const { receiptTaxTypeSetting } = useSettingReceiptTaxTypeSelect({
    settingsArray,
    handleChange,
  });
  const { allReceiptTypeSetting } = useSettingAllReceiptTypes({
    settingsArray,
    handleChange,
  });
  const { stringArrayTypeSetting } = useSettingStringArrayInput({
    settingsArray,
    handleChange,
    isSuperAdmin,
  });
  const { emailArrayTypeSetting } = useSettingEmailArrayInput({
    settingsArray,
    handleChange,
    setValidationState,
  });
  const { receiptDateEnumSetting } = useSettingReceiptDateEnum({
    settingsArray,
    handleChange,
  });
  const { receiptDescriptionEnumSetting } = useSettingReceiptDescriptionEnum({
    settingsArray,
    handleChange,
  });

  const dynamicSettings: Record<SettingType, ISettingType> = useMemo(
    () => ({
      [SettingType.switch]: booleanTypeSetting,
      [SettingType.currency]: currencyTypeSetting,
      [SettingType.language]: languageSetting,
      [SettingType.number]: numberTypeSetting,
      [SettingType.receipt_tax_type]: receiptTaxTypeSetting,
      [SettingType.all_receipt_types]: allReceiptTypeSetting,
      [SettingType.stringArray]: stringArrayTypeSetting,
      [SettingType.emailArray]: emailArrayTypeSetting,
      [SettingType.receiptDateEnum]: receiptDateEnumSetting,
      [SettingType.receiptDescriptionEnum]: receiptDescriptionEnumSetting,
    }),
    [
      settingsArray,
      languageSetting,
      currencyTypeSetting,
      booleanTypeSetting,
      receiptDateEnumSetting,
      receiptDescriptionEnumSetting,
    ],
  );
  return {
    dynamicSettings,
    currencies,
  };
};
